<template>
  <h1 class="component-heading">Tooltip</h1>
  <p class="component-description">
    When the user hovers the mouse pointer over an element, a tooltip is
    frequently used to provide further information about that object.
  </p>
  <divider :position="'horizontal'" :space="'20px'" />
  <h2 class="component-side-heading">Basic usage</h2>
  <p class="component-description">
    To define input data in tooltip use <b>:data-tooltip</b><br />
    Set the location of tooltip by giving the attribute
    <b>:data-tooltip-location</b>
  </p>
  <div class="component-example">
    <div class="example-body2">
      <button
        class="bg-default border"
        data-tooltip="tooltip"
        data-tooltip-location="top"
      >
        Top
      </button>

      <hlx-button
        class="bg-default border"
        data-tooltip="Here is an example of very long tooltip text that spans multiple lines."
        data-tooltip-location="right"
        >Right</hlx-button
      >
      <hlx-button
        class="bg-default border"
        data-tooltip="Here is an example of very long tooltip text that spans multiple lines. Note:
Tooltips are dynamically sized to fit content. However, it has a minimum and maximum width defined."
        data-tooltip-location="bottom"
        >Bottom</hlx-button
      >
      <hlx-button
        class="bg-default border"
        data-tooltip="Here is an example of very long tooltip text that spans multiple lines. Note:
Tooltips are dynamically sized to fit content. However, it has a minimum and maximum width defined."
        data-tooltip-location="left"
        >Left</hlx-button
      >
      <div class="bg-default border has-tooltip" data-tooltip-location="left">
        <i class="icon-info-circle-regular"></i>
        <div style="padding: 10px" class="tooltip-content">
          <b style="display: flex; margin-bottom: 10px; font-weight: bold"
            >Title</b
          >
          <ul>
            <li>Point 1</li>
            <li><b>Bold point</b></li>
            <li style="color: yellow">Custom style</li>
          </ul>
        </div>
      </div>
      <div class="bg-default border has-tooltip" data-tooltip-location="top">
        <i class="icon-info-circle-regular"></i>
        <div style="padding: 10px" class="tooltip-content">
          <b style="display: flex; margin-bottom: 10px; font-weight: bold"
            >Title</b
          >
          <ul>
            <li>Point 1</li>
            <li><b>Bold point</b></li>
            <li style="color: yellow">Custom style</li>
          </ul>
        </div>
      </div>
      <div class="bg-default border has-tooltip" data-tooltip-location="right">
        <i class="icon-info-circle-regular"></i>
        <div style="padding: 10px" class="tooltip-content">
          <b style="display: flex; margin-bottom: 10px; font-weight: bold"
            >Title</b
          >
          <ul>
            <li>Point 1</li>
            <li><b>Bold point</b></li>
            <li style="color: yellow">Custom style</li>
          </ul>
        </div>
      </div>
      <div class="bg-default border has-tooltip" data-tooltip-location="bottom">
        <i class="icon-info-circle-regular"></i>
        <div style="padding: 10px" class="tooltip-content">
          <b style="display: flex; margin-bottom: 10px; font-weight: bold"
            >Title</b
          >
          <ul>
            <li>Point 1</li>
            <li><b>Bold point</b></li>
            <li style="color: yellow">Custom style</li>
          </ul>
        </div>
      </div>
      <!-- <hlx-button class="bg-default border" tooltip="Bottom" flow="down" -->
      <!-- >Bottom</hlx-button
      >
      <hlx-button class="bg-default border" tooltip="Left" flow="left"
        >Left</hlx-button
      >
      <hlx-button class="bg-default border" tooltip="Right" flow="right"
        >Right</hlx-button -->
      <!-- > -->
    </div>

    <div class="example-footer">
      <span
        id="basic-tooltip-icon"
        class="icon"
        @click="showCode('basic-tooltip')"
        ><i class="icon-code-regular"></i
      ></span>
    </div>
  </div>
  <div id="basic-tooltip" class="source-code" style="display: none">
    <section class="example-source-wrapper">
      <div class="source">
        <CodeEditor
          :id="'editor'"
          :display_language="false"
          :value="basic_tooltip"
          :read_only="true"
          :theme="editor_theme"
          :languages="[
            ['Javascript', 'Vue'],
            ['javascript', 'JS'],
            ['python', 'Python'],
          ]"
        />
      </div>
    </section>
  </div>

  <divider :position="'horizontal'" :space="'20px'" />
  <h2 class="component-side-heading">Attributes</h2>
  <!-- <hlx-table-component :tableData="this.tooltipAttributes.table_data" :theader="this.tooltipAttributes.columns" :height="'max-content'"/> -->
  <!-- <hlx-table
    :data="this.table_data"
    :border="true"
    theme="secondary"
    :rowHover="true"
    :ellipses="5"
    :bold_header="true"
  >
    <hlx-column
      :prop="'attribute'"
      :label="'Name'"
      :type="'string'"
      :width="200"
    />
    <hlx-column
      :prop="'description'"
      :label="'Description'"
      :type="'string'"
      :width="300"
    />
    <hlx-column :prop="'default'" :label="'Default'" :type="'string'" />
    <hlx-column :prop="'type'" :label="'Type'" :type="'string'" />
    <hlx-column
      :prop="'accepted_values'"
      :label="'Accepted values'"
      :type="'string'"
    />
    <hlx-column :prop="'mandatory'" :label="'Mandatory'" :type="'string'" />
  </hlx-table> -->
  <hlx-table
    :column-count="6"
    :border="['table', 'header', 'vertical', 'horizontal']"
    :bold-headers="false"
    :row-hover="false"
    theme="grey"
    :striped-rows="false"
  >
    <template #thead>
      <hlx-table-head
        v-for="(i, index) in alertAttributes"
        :key="index"
        :width="i.width"
        >{{ i.label }}</hlx-table-head
      >
    </template>
    <template #tbody>
      <tr v-for="(i, index) in table_data" id="" :key="index">
        <hlx-table-cell
          v-for="(j, col_index) in alertAttributes"
          :key="col_index"
          :align="'left'"
        >
          {{ i[j.prop] }}
        </hlx-table-cell>
      </tr>
    </template>
  </hlx-table>
  <divider :space="'20px'" class="mobile-nav-divider" />
  <div class="mobile-next-page-nav">
    <span class="prev-btn" @click="prevNextPage('prev', 'tag')"
      ><i class="icon-angle-left-regular prev-icon"></i>Tag</span
    >
    <span class="nxt-btn" @click="prevNextPage('next', 'label')"
      >Label <i class="icon-angle-right-regular"></i
    ></span>
  </div>
</template>

<script>
import CodeEditor from "simple-code-editor";
import divider from "../components/DividerComponent.vue";
import hlxButton from "../components/ButtonComponent.vue";
import hlxTable from "../components/table/HlxTable.vue";
import hlxTableHead from "../components/table/HlxTableHead.vue";
import hlxTableCell from "../components/table/HlxTableCell.vue";

export default {
  name: "App",
  components: {
    divider,
    hlxButton,
    CodeEditor,
    hlxTableCell,
    hlxTableHead,
    hlxTable,
  },
  data() {
    return {
      tooltip: `<div>hi</div>`,
      alertAttributes: [
        {
          prop: "attribute",
          label: "Attributes",
          width: 100,
          type: "string",
          format: "",
        },
        {
          prop: "description",
          label: "Description",
          width: 250,
          type: "string",
          format: "",
        },
        {
          prop: "type",
          label: "Type",
          width: 50,
          type: "string",
          format: "",
        },
        {
          prop: "accepted_values",
          label: "Accepted values",
          width: 150,
          type: "string",
          format: "",
        },
        {
          prop: "default",
          label: "Default",
          width: 100,
          type: "string",
          format: "",
        },
        {
          prop: "mandatory",
          label: "Mandatory",
          width: 100,
          type: "string",
          format: "",
        },
      ],
      table_data: [
        {
          attribute: "data-tooltip",
          description:
            "specify the inputs need to be displayed inside the tooltip",
          type: "String",
          accepted_values: "-",
          default: "-",
          mandatory: "true",
        },
        {
          attribute: "data-tooltip-location",
          description: "specify the direction where tooltip to be displayed",
          type: "String",
          accepted_values: "top | left | right | bottom",
          default: "-",
          mandatory: "true",
        },
      ],
      editor_theme: "light",

      basic_tooltip: `
      <template>
      <p class="bg-default border"data-tooltip="Here is an example of very long tooltip text that spans multiple lines. Note:Tooltips are dynamically sized to fit content. However, it has a minimum and maximum width defined." data-tooltip-location="top">Top</p>
      <p class="bg-default border" data-tooltip="Here is an example of very long tooltip text that spans multiple lines." data-tooltip-location="right">Right</p>
      <p class="bg-default border" data-tooltip="Here is an example of very long tooltip text that spans multiple lines. Note: Tooltips are dynamically sized to fit content. However, it has a minimum and maximum width defined." data-tooltip-location="bottom">Bottom</p>
      <p class="bg-default border" data-tooltip="Here is an example of very long tooltip text that spans multiple lines. Note: Tooltips are dynamically sized to fit content. However, it has a minimum and maximum width defined." data-tooltip-location="left">Left</p>
      </template>
`,
    };
  },
  mounted() {
    this.$nextTick(() => {
      document.querySelectorAll(".has-tooltip").forEach((btn) => {
        const tooltip = btn.querySelector(".tooltip-content");

        btn.addEventListener("mouseenter", () => {
          const preferred = btn.getAttribute("data-tooltip-location") || "top";

          tooltip.setAttribute("data-actual-location", preferred);

          requestAnimationFrame(() => {
            const rect = tooltip.getBoundingClientRect();
            const spacing = 8;

            let newLocation = preferred;

            if (preferred === "top" && rect.top < spacing) {
              newLocation = "bottom";
            } else if (
              preferred === "bottom" &&
              rect.bottom > window.innerHeight - spacing
            ) {
              newLocation = "top";
            } else if (preferred === "left" && rect.left < spacing) {
              newLocation = "right";
            } else if (
              preferred === "right" &&
              rect.right > window.innerWidth - spacing
            ) {
              newLocation = "left";
            }

            tooltip.setAttribute("data-actual-location", newLocation);
          });
        });
      });
    });
  },
  methods: {
    prevNextPage(nav, name) {
      if (nav === "prev") {
        sessionStorage.setItem("lastname", name);
        this.$router.push({ name: name });
      } else if (nav === "next") {
        sessionStorage.setItem("lastname", name);
        this.$router.push({ name: name });
      }
    },
    showCode(val) {
      document.getElementById(val + "-icon").classList.toggle("active-icon");
      if (document.getElementById(val).style.display === "none") {
        document.getElementById(val).style.display = "block";
      } else if (document.getElementById(val).style.display === "block") {
        document.getElementById(val).style.display = "none";
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.example-body2 {
  align-items: center;
}

.has-tooltip {
  position: relative;
  z-index: 10;
}

/* Tooltip base */
.tooltip-content {
  position: absolute;
  top: auto;
  bottom: auto;
  left: auto;
  right: auto;
  background-color: #2c2c2c;
  color: #fff;
  padding: 20px;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 200;
  min-width: 150px;
  max-width: 300px;
  box-shadow: 0px 0px 24px rgba(0, 0, 0, 0.2);
  opacity: 0;
  visibility: hidden;
  transition: all 0.2s ease;
  pointer-events: none;
  white-space: normal;
  text-align: left;
}

/* Show on hover */
.has-tooltip:hover .tooltip-content {
  opacity: 1;
  visibility: visible;
}

/* Tooltip content extras */
.tooltip-content ul {
  padding-left: 18px;
  margin: 0;
}
.tooltip-content li {
  margin-bottom: 6px;
  line-height: 1.4;
}
.tooltip-content b {
  font-weight: bold;
}

/* Arrow */
.tooltip-content::after {
  content: "";
  position: absolute;
}

/* TOP */
.tooltip-content[data-actual-location="top"] {
  bottom: calc(100% + 8px);
  left: 50%;
  transform: translateX(-50%);
  transform-origin: bottom;
}
.tooltip-content[data-actual-location="top"]:hover {
  transform: translateX(-50%);
}
.tooltip-content[data-actual-location="top"]::after {
  top: 100%;
  left: 50%;
  transform: translateX(-50%);
  border-width: 6px;
  border-style: solid;
  border-color: #2c2c2c transparent transparent transparent;
}

/* BOTTOM */
.tooltip-content[data-actual-location="bottom"] {
  top: calc(100% + 8px);
  left: 50%;
  transform: translateX(-50%);
  transform-origin: top;
}
.tooltip-content[data-actual-location="bottom"]:hover {
  transform: translateX(-50%);
}
.tooltip-content[data-actual-location="bottom"]::after {
  top: -6px;
  left: 50%;
  transform: translateX(-50%) rotate(180deg);
  border-width: 6px 6px 0 6px;
  border-style: solid;
  border-color: #2c2c2c transparent transparent transparent;
}

/* LEFT */
.tooltip-content[data-actual-location="left"] {
  right: calc(100% + 8px);
  top: 50%;
  transform: translateY(-50%);
  transform-origin: right;
}
.tooltip-content[data-actual-location="left"]:hover {
  transform: translateY(-50%);
}
.tooltip-content[data-actual-location="left"]::after {
  left: 100%;
  top: 50%;
  transform: translateY(-50%);
  border-width: 6px 0 6px 6px;
  border-style: solid;
  border-color: transparent transparent transparent #2c2c2c;
}

/* RIGHT */
.tooltip-content[data-actual-location="right"] {
  left: calc(100% + 8px);
  top: 50%;
  transform: translateY(-50%);
  transform-origin: left;
}
.tooltip-content[data-actual-location="right"]:hover {
  transform: translateY(-50%);
}
.tooltip-content[data-actual-location="right"]::after {
  right: 100%;
  top: 50%;
  transform: translateY(-50%);
  border-width: 6px 6px 6px 0;
  border-style: solid;
  border-color: transparent #2c2c2c transparent transparent;
}
</style>
